import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import type {
  ITunnelValidation,
  IValidationErrorItem,
  IValidationSectionEntry,
} from 'models';

/**
 * Phase 6e1 — validation slice (§22 / R12 closure): the React home of
 * `ahu.errors` + the unit.js merge algorithms (§22.3). The error-manager
 * component renders from this slice instead of being model-owned; the
 * error→tint producer (useValidationManager) derives the §22.5 maps from it.
 *
 * Merge semantics are ported splice-for-splice from unit.js — including
 * [NEG]#66 (validateTunnel does NOT append a missing tunnel entry; the
 * `tunnelFound` flag is computed but unused in legacy).
 */

export interface ResolveSummaryCounts {
  generate: number;
  do_not_generate: number;
}

export interface AhuValidationState {
  /** `ahu.errors.errors` — per-tunnel validation tree (§22.2). */
  errors: ITunnelValidation[];
  /** validationsInProgress counter (activate/deActivateValidating locks). */
  validatingCount: number;
  /** stateStorage `error-resolution-staus` ('in_progress' | null). */
  resolutionInProgress: boolean;
  /** error.log render outputs (consumed by toolbar gating). */
  count: number;
  allowGenerate: boolean;
  countsByType: ResolveSummaryCounts;
  /** `.resolve-error-log.actived` — smart-resolve panel open. */
  resolveOpen: boolean;
  /** Post-resolve summary (`#resolved-errors-information`, 5 s). */
  resolveSummary: {
    prev: ResolveSummaryCounts;
    current: ResolveSummaryCounts;
    shownAt: number;
  } | null;
  /** Bumps on every merge — the 1 s-debounced render trigger (updateErrors). */
  revision: number;
}

const initialState: AhuValidationState = {
  errors: [],
  validatingCount: 0,
  resolutionInProgress: false,
  count: 0,
  allowGenerate: true,
  countsByType: { generate: 0, do_not_generate: 0 },
  resolveOpen: false,
  resolveSummary: null,
  revision: 0,
};

/** unit.js calculateErrorsCountAndAllowGenerate port (error.log.js:178-215). */
function recount(state: AhuValidationState): void {
  let count = 0;
  let allow = true;
  const counts: ResolveSummaryCounts = { generate: 0, do_not_generate: 0 };
  state.errors.forEach((err) => {
    const vem = err.validation_error_messages;
    if (!vem) return;
    (vem.global ?? []).forEach((e) => {
      if (e.message) {
        count += 1;
        allow = allow && e.type !== 'do_not_generate';
        if (e.type === 'generate' || e.type === 'do_not_generate') counts[e.type] += 1;
      }
    });
    (vem.sections ?? []).forEach((entry) => {
      (['accessories', 'components', 'dmounts'] as const).forEach((key) => {
        const items = entry[key];
        if (Array.isArray(items)) {
          items.forEach((item) => {
            if (item.message) {
              count += 1;
              allow = allow && item.type !== 'do_not_generate';
              if (item.type === 'generate' || item.type === 'do_not_generate')
                counts[item.type] += 1;
            }
          });
        }
      });
    });
  });
  state.count = count;
  state.allowGenerate = allow;
  state.countsByType = counts;
  state.revision += 1;
}

function findSection(
  state: AhuValidationState,
  sectionId: number
): IValidationSectionEntry | undefined {
  for (const err of state.errors) {
    const found = err.validation_error_messages?.sections?.find((s) => s.id === sectionId);
    if (found) return found;
  }
  return undefined;
}

const ahuValidationSlice = createSlice({
  name: 'ahuValidation',
  initialState,
  reducers: {
    /** validate_all_3D → errors = [...responses] (§22.3). */
    setAllErrors(state, action: PayloadAction<ITunnelValidation[]>) {
      state.errors = action.payload;
      recount(state);
    },

    /** validateTunnel — in-place replace of the matching tunnel ([NEG]#66: no append). */
    replaceTunnel(state, action: PayloadAction<ITunnelValidation>) {
      const idx = state.errors.findIndex((e) => e.id === action.payload.id);
      if (idx !== -1) state.errors[idx] = action.payload;
      recount(state);
    },

    /**
     * validateTunnelOnlyComponents — per-section `components` arrays replaced
     * from the response; vanished sections spliced, new sections appended;
     * whole tunnel appended when missing.
     */
    mergeTunnelComponents(state, action: PayloadAction<ITunnelValidation>) {
      const res = action.payload;
      const existing = state.errors.find((e) => e.id === res.id);
      if (!existing) {
        state.errors.push(res);
        recount(state);
        return;
      }
      const resSections = res.validation_error_messages?.sections ?? [];
      const curSections = existing.validation_error_messages?.sections ?? [];
      // Replace/refresh components per response section; append new sections.
      resSections.forEach((rs) => {
        const cs = curSections.find((s) => s.id === rs.id);
        if (cs) {
          cs.components = rs.components;
          cs.dmounts = rs.dmounts ?? cs.dmounts;
          cs.section = rs.section ?? cs.section;
        } else {
          curSections.push(rs);
        }
      });
      // Splice vanished sections.
      for (let i = curSections.length - 1; i >= 0; i -= 1) {
        if (!resSections.some((rs) => rs.id === curSections[i].id)) {
          curSections.splice(i, 1);
        }
      }
      existing.validation_error_messages.global =
        res.validation_error_messages?.global ?? existing.validation_error_messages.global;
      recount(state);
    },

    /**
     * validateOnNewComponent / validateOnSectionChange — replace that
     * component's section `components`; creates section/tunnel entries as
     * needed (the fragile `index` label parse preserved by the caller).
     */
    mergeComponentValidation(
      state,
      action: PayloadAction<{
        sectionId: number;
        tunnelId: number;
        sectionLabel: string;
        items: IValidationErrorItem[];
      }>
    ) {
      const { sectionId, tunnelId, sectionLabel, items } = action.payload;
      let entry = findSection(state, sectionId);
      if (!entry) {
        let tunnel = state.errors.find((e) => e.id === tunnelId);
        if (!tunnel) {
          tunnel = {
            id: tunnelId,
            index: sectionLabel.split(' ')[1]?.slice(0, -1) ?? '',
            validation_error_messages: { sections: [], global: [], errors_count: 0 },
          };
          state.errors.push(tunnel);
        }
        entry = {
          id: sectionId,
          section: sectionLabel,
          accessories: [],
          components: [],
          dmounts: [],
        };
        tunnel.validation_error_messages.sections.push(entry);
      }
      entry.components = items;
      recount(state);
    },

    /** validateOnNewAccessory — concat into the section's accessories. */
    appendAccessoryValidation(
      state,
      action: PayloadAction<{
        sectionId: number;
        tunnelId: number;
        sectionLabel: string;
        items: IValidationErrorItem[];
      }>
    ) {
      const { sectionId, tunnelId, sectionLabel, items } = action.payload;
      let entry = findSection(state, sectionId);
      if (!entry) {
        let tunnel = state.errors.find((e) => e.id === tunnelId);
        if (!tunnel) {
          tunnel = {
            id: tunnelId,
            index: sectionLabel.split(' ')[1]?.slice(0, -1) ?? '',
            validation_error_messages: { sections: [], global: [], errors_count: 0 },
          };
          state.errors.push(tunnel);
        }
        entry = {
          id: sectionId,
          section: sectionLabel,
          accessories: [],
          components: [],
          dmounts: [],
        };
        tunnel.validation_error_messages.sections.push(entry);
      }
      entry.accessories = entry.accessories.concat(items);
      recount(state);
    },

    /**
     * validateAccessoryOnChange — array-normalized: remove stale entries
     * matching returned accessory ids, then concat fresh.
     */
    mergeAccessoryOnChange(
      state,
      action: PayloadAction<
        Array<{ sectionId: number; accessoryId: number; items: IValidationErrorItem[] }>
      >
    ) {
      action.payload.forEach(({ sectionId, accessoryId, items }) => {
        const entry = findSection(state, sectionId);
        if (!entry) return;
        entry.accessories = entry.accessories
          .filter((a) => a.id !== accessoryId)
          .concat(items);
      });
      recount(state);
    },

    /** validateSectionAccessories / validateOnAccessoryDestroy — wholesale replace. */
    replaceSectionAccessories(
      state,
      action: PayloadAction<{ sectionId: number; items: IValidationErrorItem[] }>
    ) {
      const entry = findSection(state, action.payload.sectionId);
      if (entry) entry.accessories = action.payload.items;
      recount(state);
    },

    /** validateOnSplitDmountChange — per-dmount filter+push (id stamped by caller). */
    mergeDmountValidation(
      state,
      action: PayloadAction<{
        sectionId: number;
        dmountId: number;
        items: IValidationErrorItem[];
      }>
    ) {
      const entry = findSection(state, action.payload.sectionId);
      if (!entry) return;
      entry.dmounts = (entry.dmounts ?? [])
        .filter((d) => d.id !== action.payload.dmountId)
        .concat(action.payload.items);
      recount(state);
    },

    /** validateOnSplitDmountDestroy — drop that dmount's rows. */
    removeDmountErrors(
      state,
      action: PayloadAction<{ sectionId: number; dmountId: number }>
    ) {
      const entry = findSection(state, action.payload.sectionId);
      if (!entry) return;
      entry.dmounts = (entry.dmounts ?? []).filter((d) => d.id !== action.payload.dmountId);
      recount(state);
    },

    /** validateOnSectionDestroy — clear accessories (+ drop tunnel when removed). */
    clearSectionOnDestroy(
      state,
      action: PayloadAction<{ sectionId: number; tunnelRemoved?: boolean; tunnelId?: number }>
    ) {
      const { sectionId, tunnelRemoved, tunnelId } = action.payload;
      if (tunnelRemoved && tunnelId != null) {
        state.errors = state.errors.filter((e) => e.id !== tunnelId);
      } else {
        const entry = findSection(state, sectionId);
        if (entry) entry.accessories = [];
      }
      recount(state);
    },

    /** removeErrorsAfterTunnelDeleting — filter out the tunnel entry. */
    removeTunnelErrors(state, action: PayloadAction<number>) {
      state.errors = state.errors.filter((e) => e.id !== action.payload);
      recount(state);
    },

    /**
     * Drop the entries of tunnels the server no longer has (no legacy
     * counterpart — see useValidationManager).
     *
     * Every other merge above is driven by one delete telling the slice which
     * tunnel it took, which is only as good as that caller's bookkeeping: a
     * single section delete can empty — and so delete — a tunnel the board
     * never named, because the bound half of a dual-tunnel component lives in
     * another tunnel and `AHUSection.delete` sweeps it. This reconciles
     * against the live tunnel list instead, so no error can outlive the
     * tunnel it points at.
     */
    pruneTunnelErrors(state, action: PayloadAction<number[]>) {
      const live = new Set(action.payload);
      // No-op when nothing is stale: `recount` bumps `revision`, which kicks
      // the 1 s-debounced tint producer.
      if (state.errors.every((e) => live.has(e.id))) return;
      state.errors = state.errors.filter((e) => live.has(e.id));
      recount(state);
    },

    /** updateErrorsTag — in-place section label rewrite. */
    updateSectionTag(
      state,
      action: PayloadAction<{ sectionId: number; sectionLabel: string }>
    ) {
      const entry = findSection(state, action.payload.sectionId);
      if (entry) entry.section = action.payload.sectionLabel;
      state.revision += 1;
    },

    /** updateErrorsAccessoriesTag — in-place accessory item rewrite. */
    updateAccessoryTag(
      state,
      action: PayloadAction<{ sectionId: number; accessoryId: number; item: string }>
    ) {
      const entry = findSection(state, action.payload.sectionId);
      if (!entry) return;
      entry.accessories.forEach((a) => {
        if (a.id === action.payload.accessoryId) a.item = action.payload.item;
      });
      state.revision += 1;
    },

    incValidating(state) {
      state.validatingCount += 1;
    },
    decValidating(state) {
      state.validatingCount = Math.max(0, state.validatingCount - 1);
    },
    setResolutionInProgress(state, action: PayloadAction<boolean>) {
      state.resolutionInProgress = action.payload;
    },
    setResolveOpen(state, action: PayloadAction<boolean>) {
      state.resolveOpen = action.payload;
    },
    showResolveSummary(
      state,
      action: PayloadAction<{ prev: ResolveSummaryCounts; current: ResolveSummaryCounts }>
    ) {
      state.resolveSummary = { ...action.payload, shownAt: Date.now() };
    },
    clearResolveSummary(state) {
      state.resolveSummary = null;
    },
    resetValidation() {
      return initialState;
    },
  },
});

export const {
  setAllErrors,
  replaceTunnel,
  mergeTunnelComponents,
  mergeComponentValidation,
  appendAccessoryValidation,
  mergeAccessoryOnChange,
  replaceSectionAccessories,
  mergeDmountValidation,
  removeDmountErrors,
  clearSectionOnDestroy,
  removeTunnelErrors,
  pruneTunnelErrors,
  updateSectionTag,
  updateAccessoryTag,
  incValidating,
  decValidating,
  setResolutionInProgress,
  setResolveOpen,
  showResolveSummary,
  clearResolveSummary,
  resetValidation,
} = ahuValidationSlice.actions;

// ---------------------------------------------------------------------------
// Selectors (§22.5 React shape)
// ---------------------------------------------------------------------------
export const selectValidationErrors = (s: RootState) => s.ahuValidation.errors;
export const selectErrorCount = (s: RootState) => s.ahuValidation.count;
export const selectAllowGenerate = (s: RootState) => s.ahuValidation.allowGenerate;
export const selectValidating = (s: RootState) => s.ahuValidation.validatingCount > 0;
export const selectResolutionInProgress = (s: RootState) =>
  s.ahuValidation.resolutionInProgress;

/**
 * error.log render reshape (error.log.js:43-49): per-tunnel
 * `{...validation_error_messages, tunnel_id}` with 'Tunnel ' stripped labels.
 */
export interface FlatTunnelErrors {
  tunnel_id: number;
  sections: IValidationSectionEntry[];
  global: IValidationErrorItem[];
  errors_count: number;
}

export function flattenErrors(errors: ITunnelValidation[]): FlatTunnelErrors[] {
  return errors.map((err) => {
    const vem = err.validation_error_messages ?? {
      sections: [],
      global: [],
      errors_count: 0,
    };
    return {
      tunnel_id: err.id,
      global: vem.global ?? [],
      errors_count: vem.errors_count ?? 0,
      sections: (vem.sections ?? []).map((s) => ({
        ...s,
        section: String(s.section ?? '').replace('Tunnel ', ''),
      })),
    };
  });
}

export default ahuValidationSlice.reducer;
